/** Shared native overlay positioning, focus traversal, and lifecycle helpers. */
import { computePlacement } from './popover-utils.js';
import { deepActiveElement, overlayContains } from './focus-utils.js';
export { deepActiveElement, overlayContains } from './focus-utils.js';
export type OverlayPlacement = 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end' | 'left' | 'right';
export type OverlayDismissReason = 'escape' | 'outside' | 'anchor-removed' | 'ancestor-closed';

export const overlayActiveElement = deepActiveElement;

/** Traverses the composed tree in keyboard order, without duplicating slots. */
export function overlayFocusable(root: HTMLElement): HTMLElement[] {
  const elements: HTMLElement[] = [];
  const seen = new Set<Node>();
  const visit = (node: Node): void => {
    if (seen.has(node)) return;
    seen.add(node);
    if (node instanceof HTMLElement) {
      const style = getComputedStyle(node);
      if (node.hidden || node.inert || node.hasAttribute('disabled') || style.display === 'none' || style.visibility === 'hidden') return;
      // Firefox gives native dialog a default tabIndex of zero. The scope
      // container is not a control and must not become its own wrap target.
      if (node !== root && node.tabIndex >= 0 && !node.matches('input[type="hidden"]')) elements.push(node);
      if (node.localName === 'slot') {
        const assigned = (node as HTMLSlotElement).assignedNodes({ flatten: true });
        (assigned.length ? assigned : [...node.childNodes]).forEach(visit);
        return;
      }
      if (node.shadowRoot) { visit(node.shadowRoot); return; }
    }
    node.childNodes.forEach(visit);
  };
  visit(root);
  // Positive tabindex comes first, as in the platform's sequential focus order.
  return elements.map((element, order) => ({ element, order })).sort((a, b) =>
    (a.element.tabIndex > 0 ? a.element.tabIndex : Infinity) - (b.element.tabIndex > 0 ? b.element.tabIndex : Infinity) || a.order - b.order
  ).map(item => item.element);
}

interface OverlayDocumentState {
  stack: AnchoredOverlayController[];
  scroll?: { count: number; overflow: string; priority: string };
}
const stateKey = Symbol.for('qds.overlay.document-state');
/** Share coordination across standalone bundles without retaining documents. */
function overlayState(document: Document): OverlayDocumentState {
  const owner = document as Document & { [stateKey]?: OverlayDocumentState };
  return owner[stateKey] ||= { stack: [] };
}
/** Reference-counted across nested modal dialogs; restores the author's style. */
export function lockOverlayScroll(document: Document): () => void {
  const style = document.documentElement.style;
  const shared = overlayState(document);
  let state = shared.scroll;
  if (!state) {
    state = { count: 0, overflow: style.getPropertyValue('overflow'), priority: style.getPropertyPriority('overflow') };
    shared.scroll = state;
    style.setProperty('overflow', 'hidden');
  }
  state.count++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--state!.count) return;
    if (state!.overflow) style.setProperty('overflow', state!.overflow, state!.priority);
    else style.removeProperty('overflow');
    delete shared.scroll;
  };
}

export interface OverlayPosition {
  left: number; top: number; placement: OverlayPlacement; maxWidth: number; maxHeight: number;
}

/** Pure viewport collision calculation, shared by popovers and tooltips. */
export function positionOverlay(anchor: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom' | 'width' | 'height'>,
  panel: { width: number; height: number }, viewport: { width: number; height: number; left?: number; top?: number },
  requested: OverlayPlacement = 'bottom-start', gap = 8, rtl = false): OverlayPosition {
  const margin = 8;
  const minX = (viewport.left || 0) + margin, minY = (viewport.top || 0) + margin;
  const maxX = (viewport.left || 0) + viewport.width - margin, maxY = (viewport.top || 0) + viewport.height - margin;
  const width = Math.min(panel.width, Math.max(0, maxX - minX)), height = Math.min(panel.height, Math.max(0, maxY - minY));
  let placement = requested;
  const side = requested.split('-')[0];
  const available = { bottom: maxY - anchor.bottom - gap, top: anchor.top - minY - gap, left: anchor.left - minX - gap, right: maxX - anchor.right - gap };
  if (side === 'bottom' && computePlacement({ top: anchor.top - minY - gap, bottom: anchor.bottom - minY + gap } as DOMRect,
    height, maxY - minY) === 'top') placement = requested.replace('bottom', 'top') as OverlayPlacement;
  if (side === 'top' && available.top < height && available.bottom > available.top) placement = requested.replace('top', 'bottom') as OverlayPlacement;
  if (side === 'left' && available.left < width && available.right > available.left) placement = 'right';
  if (side === 'right' && available.right < width && available.left > available.right) placement = 'left';
  let left = anchor.left + (anchor.width - width) / 2;
  let top = placement.startsWith('top') ? anchor.top - height - gap : anchor.bottom + gap;
  if (placement.endsWith('start')) left = rtl ? anchor.right - width : anchor.left;
  if (placement.endsWith('end')) left = rtl ? anchor.left : anchor.right - width;
  if (placement === 'left' || placement === 'right') {
    left = placement === 'left' ? anchor.left - width - gap : anchor.right + gap;
    top = anchor.top + (anchor.height - height) / 2;
  }
  return { left: Math.max(minX, Math.min(left, maxX - width)), top: Math.max(minY, Math.min(top, maxY - height)),
    placement, maxWidth: Math.max(0, maxX - minX), maxHeight: Math.max(0, maxY - minY) };
}

export interface AnchoredOverlayOptions {
  anchor: () => HTMLElement | null;
  /** Optional viewport rectangle for context placement; real anchor retains lifecycle ownership. */
  anchorRect?: () => Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom' | 'width' | 'height'> | null;
  placement?: () => OverlayPlacement;
  offset?: () => number;
  outside?: () => boolean;
  /** Noninteractive help must not block its parent's outside dismissal. */
  outsidePassthrough?: boolean;
  escape?: () => boolean;
  onDismiss: (reason: OverlayDismissReason) => void;
}

/** Native top-layer overlay that retains DOM ownership, styles, and listeners. */
export class AnchoredOverlayController {
  private _open = false;
  private _frame = 0;
  private _resize: ResizeObserver | null = null;
  private _mutation: MutationObserver | null = null;
  private _parent: AnchoredOverlayController | null = null;
  constructor(private _host: HTMLElement, private _panel: HTMLElement, private _options: AnchoredOverlayOptions) {
    _panel.setAttribute('popover', 'manual');
    _panel.hidden = true;
  }
  get open(): boolean { return this._open; }
  show(): boolean {
    if (this._open) return true;
    const anchor = this._options.anchor();
    if (!this._host.isConnected || !anchor?.isConnected) return false;
    const overlayStack = overlayState(this._host.ownerDocument).stack;
    this._open = true;
    this._parent = [...overlayStack].reverse().find(overlay => overlayContains(overlay._panel, anchor) || overlayContains(overlay._panel, this._host)) || null;
    overlayStack.push(this);
    this._panel.hidden = false;
    if (typeof this._panel.showPopover === 'function') this._panel.showPopover();
    this.reposition();
    const document = this._host.ownerDocument;
    document.addEventListener('pointerdown', this._outside, true);
    document.addEventListener('keydown', this._keyDown, true);
    document.addEventListener('close', this._ancestorClose, true);
    document.addEventListener('scroll', this._schedule, true);
    document.defaultView?.addEventListener('resize', this._schedule);
    document.defaultView?.visualViewport?.addEventListener('resize', this._schedule);
    document.defaultView?.visualViewport?.addEventListener('scroll', this._schedule);
    this._resize = new ResizeObserver(this._schedule);
    this._resize.observe(anchor); this._resize.observe(this._panel);
    this._mutation = new MutationObserver(() => {
      if (!anchor.isConnected) { this._options.onDismiss('anchor-removed'); this.hide(); }
    });
    this._mutation.observe(document, { childList: true, subtree: true });
    const root = anchor.getRootNode();
    if (root !== document) this._mutation.observe(root, { childList: true, subtree: true });
    return true;
  }
  hide(): void {
    if (!this._open) return;
    const overlayStack = overlayState(this._host.ownerDocument).stack;
    // Close descendants before their anchor becomes hidden or disconnected.
    for (const child of [...overlayStack].reverse()) {
      if (child._parent === this) { child._options.onDismiss('ancestor-closed'); child.hide(); }
    }
    this._open = false;
    const index = overlayStack.indexOf(this);
    if (index >= 0) overlayStack.splice(index, 1);
    this._parent = null;
    if (this._panel.isConnected && typeof this._panel.hidePopover === 'function' && this._panel.matches(':popover-open')) this._panel.hidePopover();
    this._panel.hidden = true;
    const document = this._host.ownerDocument;
    document.removeEventListener('pointerdown', this._outside, true);
    document.removeEventListener('keydown', this._keyDown, true);
    document.removeEventListener('close', this._ancestorClose, true);
    document.removeEventListener('scroll', this._schedule, true);
    document.defaultView?.removeEventListener('resize', this._schedule);
    document.defaultView?.visualViewport?.removeEventListener('resize', this._schedule);
    document.defaultView?.visualViewport?.removeEventListener('scroll', this._schedule);
    this._resize?.disconnect(); this._resize = null;
    this._mutation?.disconnect(); this._mutation = null;
    if (this._frame) cancelAnimationFrame(this._frame);
    this._frame = 0;
  }
  destroy(): void { this.hide(); }
  reposition = (): void => {
    if (!this._open) return;
    const anchor = this._options.anchor();
    if (!anchor?.isConnected) { this._options.onDismiss('anchor-removed'); this.hide(); return; }
    const view = this._host.ownerDocument.defaultView!;
    const viewport = view.visualViewport;
    const dimensions = { width: viewport?.width || view.innerWidth, height: viewport?.height || view.innerHeight,
      left: viewport?.offsetLeft || 0, top: viewport?.offsetTop || 0 };
    // Clear the previous constraint before remeasuring, then clamp to viewport.
    this._panel.style.maxWidth = `${Math.max(0, dimensions.width - 16)}px`;
    this._panel.style.maxHeight = `${Math.max(0, dimensions.height - 16)}px`;
    const position = positionOverlay(this._options.anchorRect?.() || anchor.getBoundingClientRect(), this._panel.getBoundingClientRect(), dimensions,
      this._options.placement?.(), this._options.offset?.(), getComputedStyle(anchor).direction === 'rtl');
    this._panel.style.left = `${position.left}px`; this._panel.style.top = `${position.top}px`;
    this._panel.dataset.placement = position.placement;
  };
  private _schedule = (): void => {
    if (!this._frame) this._frame = requestAnimationFrame(() => { this._frame = 0; this.reposition(); });
  };
  private _outside = (event: Event): void => {
    const overlayStack = overlayState(this._host.ownerDocument).stack;
    const owner = [...overlayStack].reverse().find(overlay => !overlay._options.outsidePassthrough);
    if (owner !== this || this._options.outside?.() === false) return;
    const path = event.composedPath();
    if (!path.includes(this._panel) && !path.includes(this._host) && !path.includes(this._options.anchor()!)) this._options.onDismiss('outside');
  };
  private _keyDown = (event: KeyboardEvent): void => {
    const overlayStack = overlayState(this._host.ownerDocument).stack;
    if (event.isComposing || event.key !== 'Escape' || overlayStack[overlayStack.length - 1] !== this || this._options.escape?.() === false) return;
    event.preventDefault(); event.stopImmediatePropagation();
    this._options.onDismiss('escape');
  };
  private _ancestorClose = (event: Event): void => {
    const target = event.composedPath()[0];
    if (target instanceof Node && target !== this._host && overlayContains(target, this._options.anchor())) {
      this._options.onDismiss('ancestor-closed'); this.hide();
    }
  };
}
